import { SingleSelect } from '../../../../widgets/FormikInputFieldV2';
import * as React from 'react';
import { useSelector } from 'react-redux';
import {
  memoizedOnboardingSelectors,
  onboardingSelector,
} from '../../../../reducers/onboarding';
import {
  featureFlagSelector,
  sessionSelector,
} from '../../../../reducers/session';
import { OBHardwareType, OptionType } from '../../../../types';
import { memoizedHardwareSelectors } from '../../../../selectors';
import onboardingUtils from '../../../../reducers/onboarding/utils';
import { useFormikContext } from 'formik';
import { SensorPacketCaptureConfig } from './SensorPacketCaptureConfig';
import { HardwareConfigPanel } from './generic-series/SensorCapabilityForm';
import { Checkbox } from '../../../../widgets/FormikInputFieldV2';
import { Tooltip } from '@rtkwlf/ui';
import Icon from '../../../../widgets/Icons';
import { StandardTooltipOverlay } from '../../../../constants';

const hardwareTypeLabel: Record<
  OBHardwareType,
  { uppercaseLabel: string; lowercaseLabel: string }
> = {
  [OBHardwareType.SENSOR]: {
    uppercaseLabel: 'Sensor',
    lowercaseLabel: 'sensor',
  },
  [OBHardwareType.SCANNER]: {
    uppercaseLabel: 'Scanner',
    lowercaseLabel: 'scanner',
  },
};

type Props = {
  type: OBHardwareType;
  currentId: string;
  disabled?: boolean;
};

/**
 * Builds a single select for sensor/scanner models, including logic to
 *  - deal with the rackmount option
 *  - show options based on access control
 * Must be used within a formik context with a field "model".
 */
export const HardwareModelSelect: React.FunctionComponent<Props> = ({
  currentId,
  disabled,
  type,
}) => {
  const { values } = useFormikContext<{ model: string }>();
  const incompleteHardwareSelector = React.useMemo(
    () => memoizedHardwareSelectors.getIncompleteHardware(type, currentId),
    [type, currentId],
  );
  const incompleteHardware = useSelector(incompleteHardwareSelector);
  const availableHardware = useSelector(
    memoizedOnboardingSelectors.getAvailableHardware,
  )[type];
  const isAwnUser = useSelector(sessionSelector.isAwnUser);
  const genericSeriesEnabled = useSelector(
    onboardingSelector.showGenericSeries,
  );

  const vanillaScannerEnabled = useSelector(
    featureFlagSelector.onboarding.vanillaScanner,
  );

  const allHardwareOptions = React.useMemo(() => {
    return availableHardware.map((hardware) => ({
      label: hardware.label,
      value: hardware.hardware_key,
    }));
  }, [availableHardware]);

  const disableForNonAWN = React.useMemo(
    () =>
      !isAwnUser &&
      !!values.model &&
      onboardingUtils.getHardwareInfoByHardwareKey(
        availableHardware,
        values.model,
      )?.hidden,
    [availableHardware, values.model, isAwnUser],
  );

  const availableHardwareOptions = React.useMemo(
    () =>
      Object.keys(incompleteHardware).reduce<OptionType[]>((result, key) => {
        const hardware = onboardingUtils.getHardwareInfosByAllotmentKey(
          availableHardware,
          key,
        );
        hardware
          .filter((h) => !h.hidden || values.model === h.hardware_key)
          .forEach((h) => {
            result.push({
              label: h.label,
              value: h.hardware_key,
            });
          });
        return result;
      }, []),
    [availableHardware, incompleteHardware, values.model],
  );
  const modelOptions = React.useMemo(
    () => (isAwnUser ? allHardwareOptions : availableHardwareOptions),
    [isAwnUser, allHardwareOptions, availableHardwareOptions],
  );

  return (
    <>
      <SingleSelect
        name="model"
        id="selectModel"
        placeholder={
          'Choose a ' + hardwareTypeLabel[type].lowercaseLabel + ' model'
        }
        options={modelOptions}
        className="mb-3"
        label={hardwareTypeLabel[type].uppercaseLabel + ' Model'}
        isDisabled={disabled || disableForNonAWN}
        required={!genericSeriesEnabled || type === OBHardwareType.SCANNER}
      />
      {type === OBHardwareType.SENSOR &&
        !!values.model &&
        !genericSeriesEnabled && (
          <HardwareConfigPanel title="Hardware Configuration" titleLevel={5}>
            <SensorPacketCaptureConfig model={values.model} />
          </HardwareConfigPanel>
        )}
      {type === OBHardwareType.SCANNER &&
        !!values.model &&
        values.model === 'scanner200' &&
        vanillaScannerEnabled && (
          <HardwareConfigPanel title="Scanner Configuration" titleLevel={5}>
            <div data-cy="vanilla-device" className="ml-4 mt-3 mb-2">
              <Checkbox name="vanilla" label="Customer Configured Appliance" />
              <Tooltip
                className="ml-2"
                placement={'right'}
                overlay={
                  <StandardTooltipOverlay>
                    Hardware will be shipped with a base image and no customer,
                    region, or configuration pre-applied. Requested sensor
                    configuration will be applied by customer once sensor
                    arrives on site.
                  </StandardTooltipOverlay>
                }
              >
                <Icon.Question />
              </Tooltip>
            </div>
          </HardwareConfigPanel>
        )}
    </>
  );
};
